import { useCallback, useEffect, useRef, useState } from 'react';
import { ipApi } from '../services/api';
import { STAFF_ROLES } from '../config/plans';

function groupStaffByRole(staff) {
  const byRole = {};
  STAFF_ROLES.forEach((r) => {
    byRole[r.id] = (staff || []).filter((s) => s.role === r.id);
  });
  return byRole;
}

function buildCounts(staff, patients, queue, receipts, admissions, beds, pharmacy, lab, emr, audit) {
  const activeStaff = (staff || []).filter((s) => s.active !== false);
  const byRole = groupStaffByRole(activeStaff);
  const queueActive = (queue || []).filter((q) => !['done', 'cancelled', 'completed'].includes(q.status)).length;
  const admissionsActive = (admissions || []).filter((a) => a.status === 'admitted').length;
  const opRevenueAll = (receipts || []).reduce((s, r) => s + Number(r.amount || r.total || 0), 0);
  return {
    doctors: byRole.doctor?.length || 0,
    reception: byRole.reception?.length || 0,
    pharmacyStaff: byRole.pharmacy?.length || 0,
    labStaff: byRole.lab?.length || 0,
    billingStaff: byRole.billing?.length || 0,
    nurses: byRole.nurse?.length || 0,
    managers: byRole.manager?.length || 0,
    staff: staff?.length || 0,
    staffActive: activeStaff.length,
    patients: patients?.length || 0,
    queueActive,
    receiptsAll: receipts?.length || 0,
    opRevenueAll,
    admissionsActive,
    bedsTotal: beds?.length || 0,
    pharmacyOrders: pharmacy?.length || 0,
    labOrders: lab?.length || 0,
    emrRecords: emr?.length || 0,
    auditEvents: audit?.length || 0,
  };
}

function fromBasic(basic, hospitalId) {
  const staff = basic.staff || [];
  const staffByRole = groupStaffByRole(staff);
  const receipts = basic.recentReceipts || [];
  const beds = basic.beds || [];
  const counts = buildCounts(staff, [], [], receipts, [], beds, [], [], [], []);
  return {
    hospital: basic.hospital,
    hospitalId,
    staff,
    staffByRole,
    quotas: basic.quotas,
    stats: basic.stats || {},
    patients: [],
    queue: [],
    receipts,
    admissions: [],
    beds,
    pharmacyOrders: [],
    labOrders: [],
    emrRecords: [],
    auditLog: [],
    timeline: basic.timeline || [],
    counts,
    generatedAt: new Date().toISOString(),
    partial: true,
  };
}

/** Loads full monitor snapshot; falls back to basic hospital API if monitor route unavailable. */
export function useHospitalMonitor(hospitalId, period = 'month') {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partial, setPartial] = useState(false);
  const dataRef = useRef(null);
  dataRef.current = data;

  const reload = useCallback(async () => {
    if (!hospitalId) return;
    setLoading(true);
    setError('');
    try {
      const monitor = await ipApi.platformHospitalMonitor(hospitalId, { period });
      setData({ ...monitor, partial: false });
      setPartial(false);
    } catch (monitorErr) {
      try {
        const basic = await ipApi.platformHospital(hospitalId, { period });
        if (!basic?.hospital) throw new Error('Hospital record not in system yet.');
        const normalized = fromBasic(basic, hospitalId);
        setData(normalized);
        setPartial(true);
        setError('');
      } catch (basicErr) {
        if (dataRef.current?.hospital) {
          setPartial(true);
          setError(basicErr.message || monitorErr.message);
        } else {
          setData(null);
          setError(basicErr.message || monitorErr.message);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [hospitalId, period]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, partial, reload };
}
